import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import * as React from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useWindowSize } from 'react-use'
import { LoudspeakerIcon } from 'src/assets/icons/customIcons/header-icons/Loudspeaker'
import { AchievmentIcon } from 'src/assets/icons/customIcons/menu-icons/Achievment'
import { CalendarIcon } from 'src/assets/icons/customIcons/menu-icons/Calendar'
import { CatalogIcon } from 'src/assets/icons/customIcons/menu-icons/Catalog'
import { FeedbackIcon } from 'src/assets/icons/customIcons/menu-icons/Feedback'
import { HomeIcon } from 'src/assets/icons/customIcons/menu-icons/Home'
import { MycoursesIcon } from 'src/assets/icons/customIcons/menu-icons/Mycourses'
import PortfolioIcon from 'src/assets/icons/customIcons/menu-icons/Portfolio'
import { ProgramsIcon } from 'src/assets/icons/customIcons/menu-icons/Programs'
import { RatingIcon } from 'src/assets/icons/customIcons/menu-icons/Rating'
import { RecomendationsIcon } from 'src/assets/icons/customIcons/menu-icons/Recomendations'
import { ResumeIcon } from 'src/assets/icons/customIcons/menu-icons/Resume'
import { ScheduleIcon } from 'src/assets/icons/customIcons/menu-icons/Schedule'
import { SelectedCoursesIcon } from 'src/assets/icons/customIcons/menu-icons/SelectedCourses'
import { StatementsIcon } from 'src/assets/icons/customIcons/menu-icons/Statements'
import { useAdminAccess } from 'src/helpers/hooks'
import { useAuth } from 'src/hooks/auth/authObservableValue'
import { useCollapsibleMenu } from 'src/hooks/collapsibleMenu'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { sidebarStorage, useSidebar } from 'src/hooks/sidebar'
import { useUserState } from 'src/hooks/userState'
import MenuItem from 'src/views/components/MenuItem'
import ArgusLogo from 'src/views/includes/ArgusLogo'

export function Sidebar(): JSX.Element | null {
  const t = useTranslatable()
  const { width } = useWindowSize()
  const userState = useUserState()
  const isSidebarOpen = useSidebar()
  const activeSemester = useActiveSemester()

  const auth = useAuth()
  const { can } = useAdminAccess()
  const canViewFaculties = can('faculties')
  const canViewPrograms = can('programs')
  const canViewCourses = can('courses')
  const canViewStatements = can('statements')
  const canViewRegimes = can('regimes')
  const canViewFiles = can('files')
  const canViewMails = can('mails')
  const canViewCaches = can('caches')
  // One tab per user type the profile may view; "All" is filtered by the server.
  const userMenuItems = [
    ...(can('usersAll') ? [{ path: 'administrator/users/all', title: t('common:all') }] : []),
    ...(can('userAdministrators') ? [{ path: 'administrator/users/administrators', title: t('system:admins') }] : []),
    ...(can('userLecturers') ? [{ path: 'administrator/users/lecturers', title: t('lecturer:lecturers') }] : []),
    ...(can('userStudents') ? [{ path: 'administrator/users/students', title: t('student:students') }] : []),
  ]

  if (auth.state !== 'Authenticated') return null

  const profileStatus = auth.profile.profileStatus!.active
  const profileType = auth.profile.type
  const activeSemesterIdStr = activeSemester?.id != null ? String(activeSemester.id) : ''

  return (
    <div
      className={classNames(
        'absolute left-0 z-[11] flex text-primaryTextColor duration-[300ms] ease-in-out sm:z-50 md:sticky lg:top-0 xxs:z-[50] xs:z-[50]',
        {
          'left-[-100vw]': !isSidebarOpen && width < 768,
        }
      )}
      data-testid="sidebar"
    >
      <nav
        className={classNames(
          `h-[100vh] flex-col items-start justify-start bg-nav pt-[18px] duration-[800ms] ease-in-out ${
            isSidebarOpen ? 'w-[260px]' : 'w-[52px]'
          } `,
          { 'mobile-mode-nav': !isSidebarOpen }
        )}
      >
        <div className="flex items-center">
          <button
            className="group relative ml-3 flex h-[45px] flex-col items-center justify-center rounded-full px-[12px] pt-[6px] duration-[800ms] ease-in-out hover:bg-white md:hidden xxs:hidden xs:hidden"
            onClick={() => sidebarStorage.setValue(!isSidebarOpen)}
          >
            <div className="mb-[4px] h-px w-[15px] translate-x-[-6px] rotate-[-35deg] bg-primaryTextColor duration-300 ease-in-out" />
            <div className="mb-[4px] h-px w-[24px] bg-primaryTextColor duration-[800ms] ease-in-out" />
            <div className="mb-[4px] h-px w-[15px] translate-x-[-6px] rotate-[35deg] bg-primaryTextColor duration-300 ease-in-out" />
          </button>
          <ArgusLogo isSidebarOpen={isSidebarOpen} />
        </div>
        {profileType === 'UserProfileStudent' && (
          <ul className="mt-[17%] h-[70vh] max-h-[700px] w-full list-none overflow-y-auto overflow-x-hidden pl-0 xxs:h-[40vh] xxs:overflow-y-scroll xs:h-[60vh] xs:overflow-y-scroll">
            <MenuItem icon={<HomeIcon />} title={t('system:home_page')} defaultPath="student" end />
            {profileStatus && (
              <>
                <MenuItem
                  icon={<SelectedCoursesIcon />}
                  title={t('course:selected_courses')}
                  defaultPath="student/choices"
                />
                <MenuItem icon={<MycoursesIcon />} title={t('course:my_courses')} defaultPath="student/courses" />
                <MenuItem icon={<CatalogIcon />} title={t('course:courses_catalog')} defaultPath="student/catalog" />
                <MenuItem icon={<ProgramsIcon />} title={t('program:my_programs')} defaultPath="student/programs" />
                <MenuItem icon={<CalendarIcon />} title={t('calendar:calendar')} defaultPath="student/calendar" />
                <MenuItem icon={<ScheduleIcon />} title={t('schedule:my_schedule')} defaultPath="student/schedule" />
              </>
            )}
            <MenuItem icon={<StatementsIcon />} title={t('statement:statements')} defaultPath="student/statements" />
            {userState.data?.regime.functionalIsLimited === false && (
              <MenuItem icon={<LoudspeakerIcon />} title={t('news:news')} defaultPath="news" />
            )}
            <MenuItem
              icon={<ResumeIcon />}
              title={t('resume:resume')}
              defaultPath="student/resume"
              isStudent
              pages={[
                {
                  link: 'student/resume/personal',
                  title: t('person:personal_information'),
                },
                {
                  link: 'student/resume/education',
                  title: t('resume:education'),
                },
                {
                  link: 'student/resume/work',
                  title: t('resume:working_experience'),
                },
                {
                  link: 'student/resume/trainings',
                  title: t('resume:trainings_seminars_workshops'),
                },
                {
                  link: 'student/resume/skills',
                  title: t('resume:personal_skills'),
                },
                {
                  link: 'student/resume/additional',
                  title: t('resume:additional_info'),
                },
                {
                  link: 'student/resume/summary',
                  title: t('resume:resume_summary'),
                },
              ]}
            />
            <MenuItem icon={<AchievmentIcon />} title={t('achievement:achievement_sheet')} defaultPath="student/card" />

            {profileStatus &&
              userState.data?.regime.functionalIsLimited === false &&
              userState.data?.portfolioExists && (
                <MenuItem
                  icon={<PortfolioIcon />}
                  title={t('portfolio:portfolio')}
                  defaultPath="student/portfolio"
                  isStudent
                  betaSignIcon={true}
                  pages={[
                    {
                      link: 'student/portfolio/sections',
                      title: t('portfolio:general'),
                    },
                  ]}
                />
              )}

            <MenuItem
              icon={<RecomendationsIcon />}
              title={t('recommendation:recommendations')}
              defaultPath="student/recommendations"
            />
            <MenuItem icon={<RatingIcon />} title={t('person:rating')} defaultPath="student/rating" />
            {/* <MenuItem icon={<FeedbackIcon />} title={t('feedback:feedback')} defaultPath="student/feedbacks" /> */}
          </ul>
        )}
        {profileType === 'UserProfileLecturer' && (
          <ul className="mt-[17%] w-full list-none pl-0">
            <MenuItem icon={<HomeIcon />} title={t('system:home_page')} defaultPath="lecturer" end />
            <MenuItem
              icon={<CatalogIcon />}
              title={t('course:learning_courses')}
              defaultPath="lecturer/courses/teaches"
            />
            <MenuItem
              icon={<StatementsIcon />}
              title={t('syllabus:syllabuses')}
              defaultPath="lecturer/courses/manage"
            />
            <MenuItem icon={<CalendarIcon />} title={t('calendar:calendar')} defaultPath="lecturer/calendar" />
            <MenuItem icon={<ScheduleIcon />} title={t('schedule:my_schedule')} defaultPath="lecturer/schedule" />

            {profileStatus &&
              userState.data?.regime.functionalIsLimited === false &&
              userState.data?.portfolioExists && (
                <MenuItem
                  icon={<PortfolioIcon />}
                  title={t('portfolio:portfolio')}
                  defaultPath="lecturer/portfolio/sections"
                  betaSignIcon={true}
                  pages={[
                    {
                      link: 'lecturer/portfolio/sections',
                      title: t('portfolio:general'),
                    },
                  ]}
                />
              )}

            <MenuItem icon={<FeedbackIcon />} title={t('feedback:feedback')} defaultPath="lecturer/feedbacks" />
          </ul>
        )}
        {profileType === 'UserProfileAdministrator' && (
          <ul className="mt-[17%] h-[80vh] max-h-[900px] w-full list-none overflow-y-auto overflow-x-hidden pl-0 xxs:h-[40vh] xxs:overflow-y-scroll xs:h-[60vh] xs:overflow-y-scroll">
            {/* <MenuItem
              icon={<FontAwesomeIcon icon={FreeBrandsSvgIcons.faBandcamp} />}
              title={t('system:home_page')}
              defaultPath="administrator"
              end
            /> */}
            {(canViewFaculties || canViewPrograms || canViewCourses || canViewStatements) && (
              <CollapsibleMenuItem
                icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faUsers} />}
                title={t('system:administration_sidebar')}
                menuKey="adminAdministration"
                defaultPath="administrator/administration"
                items={[
                  ...(canViewFaculties
                    ? [{ path: 'administrator/administration/faculties', title: t('faculty:faculties') }]
                    : []),
                  ...(canViewPrograms
                    ? [{ path: 'administrator/administration/programs', title: t('program:programs') }]
                    : []),
                  ...(canViewCourses
                    ? [
                        {
                          path: 'administrator/administration/courses',
                          title: t('course:learning_courses'),
                          search:
                            activeSemesterIdStr !== ''
                              ? `?semId=${encodeURIComponent(activeSemesterIdStr)}&typePref=first`
                              : '?typePref=first',
                        },
                      ]
                    : []),
                  ...(canViewStatements
                    ? [{ path: 'administrator/administration/statements', title: t('statement:statements') }]
                    : []),
                ]}
              />
            )}

            {can('schedules') && (
              <MenuItem
                icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faTable} />}
                title={t('schedule:schedules')}
                defaultPath="administrator/schedules"
              />
            )}

            {can('statistics') && (
              <CollapsibleMenuItem
                icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faChartPieAlt} />}
                title={t('system:sidebar_statistics')}
                menuKey="adminDataAndStatistics"
                defaultPath="administrator/statistics"
                items={[
                  {
                    path: 'administrator/statistics',
                    title: t('system:sidebar_general'),
                    excludePaths: ['administrator/statistics/graduates'],
                  },
                  { path: 'administrator/statistics/graduates', title: t('statistics:graduates') },
                ]}
              />
            )}

            {userMenuItems.length > 0 && (
              <CollapsibleMenuItem
                icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faUsers} />}
                title={t('system:users')}
                menuKey="adminUsers"
                defaultPath="administrator/users/all"
                items={userMenuItems}
              />
            )}

            {can('portfolio') && (
              <CollapsibleMenuItem
                icon={<PortfolioIcon className="ml-[-2px]" />}
                title={t('portfolio:portfolio')}
                menuKey="adminPortfolio"
                defaultPath="administrator/portfolio/sections"
                items={[
                  { path: 'administrator/portfolio/sections', title: t('portfolio:sections') },
                  { path: 'administrator/portfolio/competencies', title: t('portfolio:competencies') },
                  {
                    path: 'administrator/portfolio/criterias',
                    title: t('portfolio:competency_criterias'),
                  },
                  { path: 'administrator/portfolio/students', title: t('student:students') },
                  { path: 'administrator/portfolio/works', title: t('portfolio:works') },
                  { path: 'administrator/portfolio/limits', title: t('portfolio:limits') },
                ]}
              />
            )}

            {can('bulkMessages') && (
              <CollapsibleMenuItem
                icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faEnvelope} />}
                title={t('bulk_message:messages')}
                menuKey="adminMessages"
                defaultPath="administrator/messages/bulk"
                items={[{ path: 'administrator/messages/bulk', title: t('bulk_message:bulk_message') }]}
              />
            )}

            {can('statementTemplates') && (
              <CollapsibleMenuItem
                icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faCircleInfo} />}
                title={t('system:general_information')}
                menuKey="adminGeneralInformation"
                defaultPath="administrator/general/statement-templates"
                items={[
                  { path: 'administrator/general/statement-templates', title: t('statement:statement_templates') },
                ]}
              />
            )}

            {(canViewRegimes || canViewFiles || canViewMails || canViewCaches) && (
              <CollapsibleMenuItem
                icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faLock} />}
                title={t('system:sensitive_data')}
                menuKey="adminSensitiveData"
                defaultPath="administrator/sensitive/regimes"
                items={[
                  ...(canViewCaches ? [{ path: 'administrator/sensitive/caches', title: t('caches:caches') }] : []),
                  ...(canViewRegimes
                    ? [{ path: 'administrator/sensitive/regimes', title: t('admin_regimes:regimes') }]
                    : []),
                  ...(canViewFiles ? [{ path: 'administrator/sensitive/files', title: t('common:files') }] : []),
                  ...(canViewMails ? [{ path: 'administrator/sensitive/mails', title: t('common:mails') }] : []),
                ]}
              />
            )}
          </ul>
        )}
        {/* <LocaleSwitcher mobile={isSidebarOpen} /> */}
      </nav>
      <div className="block h-screen w-[calc(98vw-260px)] md:hidden" onClick={() => sidebarStorage.setValue(false)} />
    </div>
  )
}

// Collapsible Menu Item Component
interface CollapsibleMenuItemProps {
  readonly icon: React.ReactNode
  readonly title: string
  readonly menuKey: string
  readonly items: readonly {
    path: string
    title: string
    search?: string
    /** Nested paths that have their own item, so this item must not also look active there. */
    excludePaths?: readonly string[]
  }[]
  readonly defaultPath: string
}

function CollapsibleMenuItem({ icon, title, menuKey, items, defaultPath }: CollapsibleMenuItemProps): JSX.Element {
  const location = useLocation()
  const locale = useLocale()
  const isSidebarOpen = useSidebar()
  const navigate = useNavigate()
  // Check if any child route is active
  const isChildActive = items.some((item) => location.pathname.includes(item.path))

  // Persisted collapsible state - default to open if child is active
  const [storedIsOpen, setStoredIsOpen] = useCollapsibleMenu(menuKey, isChildActive)

  // Track previous isChildActive to detect navigation TO a child route
  const prevIsChildActiveRef = React.useRef(isChildActive)

  // Auto-expand only when navigating TO a child route (not when already on one)
  React.useEffect(() => {
    const wasChildActive = prevIsChildActiveRef.current
    prevIsChildActiveRef.current = isChildActive

    // Only expand if we just navigated TO a child route from somewhere else
    if (isChildActive && !wasChildActive) {
      setStoredIsOpen(true)
    }
  }, [isChildActive, setStoredIsOpen])

  const handleToggle = React.useCallback(() => {
    setStoredIsOpen(!storedIsOpen)
  }, [storedIsOpen, setStoredIsOpen])

  // Items are already filtered by access, so the first one is always openable; defaultPath may not be.
  const firstItem = items[0]
  const handleNavigate = React.useCallback(() => {
    navigate(firstItem != null ? `${firstItem.path}${firstItem.search ?? ''}` : defaultPath)
  }, [navigate, firstItem, defaultPath])

  return (
    <li className="relative">
      {/* Main button - works as toggle when sidebar is open */}
      <button
        type="button"
        onClick={isSidebarOpen ? handleToggle : handleNavigate}
        className={classNames(
          'peer flex w-full cursor-pointer items-center justify-between px-5 transition-colors duration-0 hover:bg-hover-nav',
          { 'active-sidebar-item bg-hover-nav': isChildActive },
          { 'pl-0': !isSidebarOpen }
        )}
      >
        <div className="anchor flex h-[42px] items-center justify-start duration-0">
          <span className="asset-left-space">{icon}</span>
          <span className="link-text ml-2 duration-0">{title}</span>
        </div>
        <FontAwesomeIcon
          icon={ProRegularSvgIcons.faChevronDown}
          className={classNames('link-text h-3 w-3 transition-transform duration-200', {
            'rotate-180': storedIsOpen,
          })}
        />
      </button>

      {/* Expanded submenu - visible when sidebar is open */}
      {isSidebarOpen && (
        <ul
          className={classNames('overflow-hidden transition-all duration-200 ease-in-out', {
            'max-h-0': !storedIsOpen,
            'max-h-50': storedIsOpen,
          })}
        >
          {items.map((item) => (
            <li key={item.path}>
              <NavLink
                to={{
                  pathname: `/${locale}/${item.path}`,
                  search: item.search ?? '',
                }}
                className={({ isActive }) =>
                  classNames(
                    'block py-2.5 pl-11 pr-5 text-sm transition-colors hover:bg-hover-nav',
                    isActive && !(item.excludePaths ?? []).some((path) => location.pathname.includes(path))
                      ? 'active-sidebar-item bg-hover-nav'
                      : 'text-primaryTextColor/80'
                  )
                }
              >
                {item.title}
              </NavLink>
            </li>
          ))}
        </ul>
      )}

      {/* Hover popup - visible when sidebar is collapsed */}
      {!isSidebarOpen && (
        <div className="invisible absolute left-[55px] top-0 z-50 h-fit min-w-[180px] whitespace-nowrap rounded-DEFAULT border border-primaryTextColor/10 bg-primaryWhite shadow-2xl duration-[50ms] ease-in-out hover:!visible peer-hover:!visible sm:hidden xxs:hidden xs:hidden">
          {/* Header */}
          <div className="border-b border-primaryTextColor/10 bg-card px-4 py-2.5">
            <span className="font-semibold text-primaryTextColor">{title}</span>
          </div>

          {/* Menu items */}
          <ul className="py-1">
            {items.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={{
                    pathname: `/${locale}/${item.path}`,
                    search: item.search ?? '',
                  }}
                  className={({ isActive }) =>
                    classNames('block px-4 py-2 text-sm transition-colors', {
                      'bg-seconderyRed text-white': isActive,
                      'text-primaryTextColor hover:bg-card': !isActive,
                    })
                  }
                >
                  {item.title}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  )
}
